<template>
    <div class="mt-3">
        <template v-if="showDescription">
            <div class="ps-0 d-sm-none mb-3">Year
                <Dropdown v-model="selectedPlanYear" :items="planYearOptions" :optional="false" description=""
                    @update:model-value="onPlanYearSelected">
                </Dropdown>
                {{ selectedPlanYearText }}

                <div class="float-end btn-group" role="group">
                    <button v-if="incrementButtonsVisible" class="btn btn-outline-secondary btn-sm border-end-0" @click="onDecrementClicked"
                        :disabled="editButtonsDisabled">
                        <i class="bi bi-dash-lg d-inline"></i></button>

                    <button class="btn btn-outline-secondary btn-sm" @click="onEditClicked"
                        :disabled="editButtonsDisabled">Edit</button>

                    <button v-if="incrementButtonsVisible" class="btn btn-outline-secondary btn-sm border-start-0" @click="onIncrementClicked"
                        :disabled="editButtonsDisabled">
                        <i class="bi bi-plus-lg d-inline"></i></button>
                </div>
            </div>
            <div class="ps-0 d-none d-sm-block">Year
                {{ planYear + 1 }}
                {{ selectedPlanYearText }}
            </div>
        </template>
        <p v-else>&nbsp;</p>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { getAgeByPlanYear } from "@/guiide/ts/utilities";
import { getCalendarYearText } from "@/guiide/ts/wants/wantsUtilities";
import { formatter } from "@/shared/ts/formatter";
import Dropdown from '../common/Dropdown.vue';

const props = defineProps({
    showDescription: { type: Boolean, default: false },
    planYears: { type: Number, default: 0 },
    initialPlanningYear: { type: Number, default: new Date().getFullYear() },
    retiringOnJanFirst: { type: Boolean, default: false },
    planYear: { type: Number, default: 0 },
    amount: { type: Number, default: 0 },
    editDisabled: { type: Boolean, default: true },
    incrementButtonsVisible: { type: Boolean, default: true }
});

const emit = defineEmits({
    'yearSelected': null,
    'edit': null,
    'decrement': null,
    'increment': null
});

watch(() => props.planYear, (newPlanYear: number) => { selectedPlanYear.value = (newPlanYear + 1).toString() });

watch(() => props.editDisabled, (newEditDisabled: boolean) => {
    editButtonsDisabled.value = newEditDisabled;
});

const selectedPlanYear = ref("1");
const editButtonsDisabled = ref(false);

const planYearOptions = computed<Array<string>>(() => {
    return Array.from({ length: props.planYears }, (_, i) => (i + 1).toString());
});

const selectedPlanYearText = computed<string>(() => {
    if (!props.showDescription) {
        return "";
    }

    const annualIncomeFmtd = formatter.formatCurrency(props.amount);
    const calendarYear = props.initialPlanningYear + props.planYear;
    const calendarYearText = getCalendarYearText(calendarYear, props.retiringOnJanFirst);
    const ageInPlanYear = getAgeByPlanYear(props.planYear);

    return ` - age ${ageInPlanYear} in ${calendarYearText} with an income of ${annualIncomeFmtd}`;
});

function onDecrementClicked() {
    emit('decrement');
}

function onIncrementClicked() {
    emit('increment');
}

function onPlanYearSelected(selectedYear: string) {
    emit('yearSelected', parseInt(selectedYear) - 1);
}

function onEditClicked() {
    emit('edit');
}

</script>

<style scoped>
::v-deep .dropdown {
    text-align: center;
    padding: 0.175rem 0.15rem;
    border: 1px solid #6c757d;
    border-radius: 0.375rem;
    transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
}

.btn-group {
    margin-top: -3px;
}
</style>